export const LEGAL_LAST_UPDATED = 'June 3, 2026';
export const LEGAL_ENTITY = 'an individual project';
export const CONTACT_EMAIL = 'contact@prepxai.in';
export const REGISTERED_ADDRESS = '......';
export const CORPORATE_ADDRESS = '......';

export const AI_ACKNOWLEDGEMENT =
  'I understand that prepxai uses AI to evaluate interview responses and that AI-generated feedback, scores, and recommendations may contain inaccuracies.';

const companyContactSection = {
  heading: 'Company Details and Contact',
  paragraphs: [
    `prepxai is operated as ${LEGAL_ENTITY}.`,
    `Registered address: ${REGISTERED_ADDRESS}.`,
    `Corporate address: ${CORPORATE_ADDRESS}.`,
    `Contact email: ${CONTACT_EMAIL}.`,
  ],
};

export const privacyPolicy = {
  title: 'Privacy Policy',
  eyebrow: 'Legal',
  summary:
    'This Privacy Policy explains how prepxai collects, uses, shares, retains, and deletes personal data when users access the platform.',
  lastUpdated: LEGAL_LAST_UPDATED,
  sections: [
    {
      heading: '1. Introduction',
      paragraphs: [
        `prepxai is an AI-powered interview preparation and hiring platform operated by ${LEGAL_ENTITY} ("prepxai", "Company", "we", "us", or "our").`,
        'This Privacy Policy applies to our website, applications, interview workflows, coding environments, resume tools, AI feedback tools, pilot programs, beta programs, and related services.',
        'By creating an account, using prepxai, uploading a resume, participating in an interview, submitting code, using voice input, or joining a pilot or beta program, you agree to the collection and processing described in this Privacy Policy.',
      ],
    },
    {
      heading: '2. Definitions',
      paragraphs: ['For clarity, the following terms are used in this Privacy Policy:'],
      bullets: [
        '"Account Data" means information used to create and manage a prepxai account, including name, email address, username, authentication details, profile settings, subscription status, and support history.',
        '"AI Output" means AI-generated feedback, scores, reports, recommendations, summaries, analysis, suggested improvements, readiness signals, and similar outputs.',
        '"Candidate" means a person who participates in an interview, assessment, coding round, practice session, pilot, beta, or hiring workflow through prepxai.',
        '"Coding Submission" means code, answers, test results, language selections, editor activity, and related information submitted during coding interviews or practice workflows.',
        '"Data Principal" has the meaning given under applicable Indian data protection law and generally means the individual to whom personal data relates.',
        '"Interview Data" means interview transcripts, prompts, responses, coding submissions, interview settings, timestamps, session metadata, reports, scores, and feedback generated through prepxai.',
        '"Personal Data" means data about an identifiable individual, including account data, resume data, transcripts, coding submissions, and usage data where it identifies or can reasonably identify a person.',
        '"Processing" means collecting, recording, organizing, storing, using, sharing, analyzing, adapting, retrieving, deleting, or otherwise handling personal data.',
        '"Resume Data" means files, text, profile details, education, work experience, skills, achievements, and other information submitted through a resume upload or resume-related feature.',
        '"Service Providers" means third parties who help us provide, host, analyze, secure, support, or improve prepxai.',
        '"Speech-to-Text" means the temporary processing of speech input to convert spoken responses into text for interview functionality.',
        '"Transcript" means text generated from typed responses or speech-to-text conversion during an interview or practice session.',
        '"Usage Data" means technical, diagnostic, analytics, device, browser, log, interaction, feature usage, and performance data.',
        '"User", "you", or "your" means any person or organization accessing or using prepxai.',
      ],
    },
    {
      heading: '3. Information We Collect',
      paragraphs: ['We may collect the following categories of information depending on how you use prepxai:'],
      bullets: [
        'Name, email address, username, password-related authentication information, and account preferences.',
        'Resume data if you upload a resume or use resume-based features.',
        'Interview transcripts generated from typed responses or speech-to-text conversion.',
        'Coding submissions, code execution data, test results, and coding environment activity.',
        'Interview reports, scores, feedback, recommendations, readiness signals, and AI-generated summaries.',
        'Interview configuration choices, such as role, target company, duration, difficulty, topics, persona, and interview type.',
        'Browser, device, IP address, operating system, approximate location inferred from technical data, cookies, local storage, and similar technical identifiers.',
        'Usage analytics, product interaction data, error logs, security logs, performance information, and support communications.',
        'Payment, subscription, coupon, referral, or billing metadata if paid services are enabled.',
        'Pilot, beta, survey, waitlist, feedback, dispute, and customer support information.',
      ],
    },
    {
      heading: '4. Voice Input and Speech-to-Text',
      paragraphs: [
        'prepxai may allow users to answer interview questions through voice input. When you use a voice feature, your speech may be temporarily processed for speech-to-text conversion so the platform can understand your answer.',
        'Only the resulting transcript is retained for interview functionality, evaluation, report generation, scoring, feedback, quality review, support, analytics, and platform improvement.',
        'prepxai does not retain raw audio after speech-to-text processing is completed.',
      ],
    },
    {
      heading: '5. How We Use Personal Data',
      paragraphs: ['We use personal data for the following purposes:'],
      bullets: [
        'To create, verify, secure, and manage user accounts.',
        'To provide AI interview practice, coding interviews, resume analysis, reports, scores, recommendations, and related product features.',
        'To convert speech input into text where users choose to use voice features.',
        'To generate, store, display, and update transcripts, reports, scores, and progress history.',
        'To personalize interview settings, practice recommendations, learning paths, readiness insights, and product experience.',
        'To operate pilot, beta, early-access, research, and feedback programs.',
        'To provide customer support, respond to disputes, investigate issues, and communicate service updates.',
        'To monitor reliability, security, abuse, fraud, errors, performance, and platform availability.',
        'To improve prepxai, including model prompts, evaluation workflows, user experience, quality assurance, and internal analytics.',
        'To comply with applicable law, legal process, regulatory obligations, tax, accounting, audit, and enforcement requests.',
        'To enforce our Terms of Service and protect the rights, safety, and property of prepxai, users, candidates, organizations, and the public.',
      ],
    },
    {
      heading: '6. AI Processing and Evaluation Limits',
      paragraphs: [
        'prepxai uses AI systems to process interview transcripts, coding submissions, resumes, prompts, interview context, and related data.',
        'AI-generated feedback, scores, reports, recommendations, summaries, and readiness signals may contain inaccuracies, omissions, hallucinations, bias, outdated information, or incomplete reasoning.',
        'prepxai outputs are intended for interview preparation, learning, coaching, and decision-support only. They should not be treated as legal, employment, educational, professional, career, or hiring advice.',
        'Users and organizations must apply independent judgment and appropriate human review before relying on any AI output.',
      ],
    },
    {
      heading: '7. Legal Bases and Consent',
      paragraphs: [
        'Where applicable, we process personal data based on user consent, performance of a contract, legitimate business purposes, legal compliance, security needs, or other grounds permitted by applicable law.',
        'For Indian users, prepxai aims to handle digital personal data in line with the Digital Personal Data Protection Act, 2023 and related rules as they become applicable to our operations.',
        'You may withdraw consent or request deletion of eligible stored data by contacting us. Withdrawing consent may limit or prevent access to certain prepxai features.',
      ],
    },
    {
      heading: '8. Third-Party Providers',
      paragraphs: [
        'prepxai may use third-party service providers to host, process, secure, analyze, support, and improve the platform.',
        'These providers may include AI model providers, speech-to-text providers, cloud hosting providers, analytics providers, logging providers, payment providers, email providers, and security providers.',
      ],
      bullets: [
        'OpenAI.',
        'Google/Gemini.',
        'OpenRouter.',
        'Sarvam AI.',
        'Cloud hosting providers.',
        'Analytics, monitoring, logging, database, email, payment, and infrastructure providers.',
      ],
      closingParagraphs: [
        'Service providers may process data only as needed to provide services to prepxai, subject to their own terms, security practices, and applicable law.',
      ],
    },
    {
      heading: '9. Data Sharing and Disclosure',
      paragraphs: ['We may share personal data in the following situations:'],
      bullets: [
        'With service providers who help us operate prepxai.',
        'With an organization, recruiter, interviewer, hiring partner, or administrator if you participate in an organization-sponsored interview, pilot, hiring process, or assessment.',
        'With a user or organization that lawfully requested or configured an interview workflow involving you.',
        'With professional advisers, auditors, legal counsel, insurers, or accountants.',
        'With courts, regulators, government authorities, law enforcement, or other parties where required by law or legal process.',
        'To investigate fraud, abuse, security incidents, policy violations, or technical issues.',
        'In connection with a merger, acquisition, financing, restructuring, sale of assets, or similar corporate transaction.',
        'With your consent or at your direction.',
      ],
      closingParagraphs: [
        'We do not sell personal data in the ordinary meaning of selling a user database for money.',
      ],
    },
    {
      heading: '10. Organization and Hiring Use',
      paragraphs: [
        'If an employer, recruiter, educational institution, training partner, or other organization uses prepxai to assess candidates, that organization is responsible for providing any required notices, obtaining any required consents, using AI outputs lawfully, and applying appropriate human review.',
        'prepxai does not guarantee that AI evaluations are accurate, complete, fair, bias-free, or suitable as the sole basis for hiring, promotion, rejection, admissions, or employment decisions.',
      ],
    },
    {
      heading: '11. Cookies and Similar Technologies',
      paragraphs: [
        'We use cookies, local storage, pixels, SDKs, and similar technologies for authentication, security, preferences, analytics, performance, caching, and product improvement.',
        'You can manage cookie preferences through our cookie banner or browser settings. Some features may not work correctly if essential cookies are blocked.',
      ],
    },
    {
      heading: '12. Data Retention',
      paragraphs: [
        'We retain personal data only for as long as reasonably necessary for the purposes described in this Privacy Policy, unless a longer period is required or permitted by law.',
        'Stored platform data may include account information, interview transcripts, coding submissions, interview reports, scores, resume data if uploaded, support records, security logs, and usage analytics.',
      ],
      bullets: [
        'Account data is generally retained while your account is active or as needed to provide the service.',
        'Transcripts, coding submissions, reports, scores, and resume data may be retained so users can access interview history, progress, and reports.',
        'Usage analytics and diagnostic logs may be retained to improve security, reliability, fraud prevention, and product performance.',
        'Aggregated, anonymized, or de-identified data may be retained for analytics, research, benchmarking, and product improvement.',
        'Backup copies may remain for a limited period until overwritten or deleted through normal backup cycles.',
        'We may retain limited information where needed for legal compliance, tax, audit, dispute resolution, fraud prevention, security, enforcement, or legitimate business records.',
      ],
    },
    {
      heading: '13. Deletion, Correction, and Access Requests',
      paragraphs: [
        `You may request access, correction, update, withdrawal of consent, or deletion of eligible stored personal data by contacting ${CONTACT_EMAIL}.`,
        'Users may request deletion of stored transcripts, account data, uploaded resume data, interview reports, and related eligible personal data.',
        'We may ask you to verify your identity before processing a request. We may refuse or limit a request where permitted by law, including where retention is required for legal, security, fraud prevention, tax, audit, dispute, or compliance reasons.',
      ],
    },
    {
      heading: '14. Security',
      paragraphs: [
        'We use reasonable technical and organizational measures designed to protect personal data against unauthorized access, loss, misuse, alteration, and disclosure.',
        'No online service, cloud system, AI provider, or transmission method is completely secure. You are responsible for keeping your login credentials confidential and for using secure devices and networks.',
      ],
    },
    {
      heading: '15. Cross-Border Processing',
      paragraphs: [
        'prepxai and its service providers may process data in India and other jurisdictions where our infrastructure, AI providers, support providers, or cloud providers operate.',
        'By using prepxai, you understand that personal data may be transferred to and processed in countries outside your location, subject to applicable law and service-provider safeguards.',
      ],
    },
    {
      heading: '16. Children and Minors',
      paragraphs: [
        'prepxai is intended for users who are at least 18 years old.',
        'If you are under 18, you may use prepxai only with consent and supervision from a parent or legal guardian. Organizations using prepxai with minors are responsible for obtaining any required parental or guardian consent.',
      ],
    },
    {
      heading: '17. User Duties',
      paragraphs: ['When using prepxai, you agree to:'],
      bullets: [
        'Provide accurate information and keep account details current.',
        'Use prepxai only for lawful purposes.',
        'Avoid uploading confidential, sensitive, or third-party information unless you have the right to do so.',
        'Review AI outputs independently before relying on them.',
        'Avoid submitting malicious code, harmful content, or unlawful material.',
        'Comply with applicable laws, policies, and consent requirements.',
      ],
    },
    {
      heading: '18. Updates to This Privacy Policy',
      paragraphs: [
        'We may update this Privacy Policy from time to time. Updated versions will be posted on the website with a revised last updated date.',
        'Your continued use of prepxai after an updated Privacy Policy is posted means you accept the updated policy, unless applicable law requires separate consent.',
      ],
    },
    companyContactSection,
  ],
};

export const termsOfService = {
  title: 'Terms of Service',
  eyebrow: 'Legal',
  summary:
    'These Terms govern access to prepxai, including AI interview practice, coding interviews, resume features, beta programs, and hiring workflows.',
  lastUpdated: LEGAL_LAST_UPDATED,
  sections: [
    {
      heading: '1. Agreement to Terms',
      paragraphs: [
        `These Terms of Service ("Terms") are a legally binding agreement between you and ${LEGAL_ENTITY}, operating prepxai.`,
        'By accessing or using prepxai, creating an account, signing up with Google or email, uploading a resume, participating in an interview, submitting code, using voice input, joining a beta or pilot, or using any related service, you agree to these Terms, our Privacy Policy, Cookie Policy, Refund Policy, Interview Transcription and AI Processing Consent, and Beta/Pilot Program Disclaimer.',
        'If you do not agree to these Terms, do not use prepxai.',
      ],
    },
    {
      heading: '2. Definitions',
      paragraphs: ['The following definitions apply to these Terms:'],
      bullets: [
        '"AI Output" means AI-generated feedback, scores, reports, recommendations, summaries, analysis, and related outputs.',
        '"Beta Services" means any pilot, beta, preview, alpha, early-access, trial, experimental, or limited-release prepxai feature or service.',
        '"Candidate" means a person who participates in an interview, coding assessment, resume review, practice session, or hiring workflow.',
        '"Content" means text, resumes, transcripts, code, files, messages, prompts, feedback, reports, scores, images, metadata, and other materials.',
        '"Organization" means an employer, recruiter, educational institution, training partner, hiring partner, customer, or other entity using prepxai.',
        '"Platform" or "Service" means prepxai websites, applications, dashboards, APIs, interview panels, coding environments, resume tools, AI features, reports, beta services, and related services.',
        '"User Content" means content submitted, uploaded, entered, generated, transmitted, or provided by or on behalf of a user, candidate, or organization.',
        '"User", "you", or "your" means any individual or organization using prepxai.',
      ],
    },
    {
      heading: '3. Eligibility',
      paragraphs: [
        'You must be at least 18 years old to use prepxai.',
        'If you are under 18, you may use prepxai only with consent and supervision from a parent or legal guardian. By using prepxai, you represent that you meet these requirements.',
      ],
    },
    {
      heading: '4. Account Registration',
      paragraphs: [
        'You must provide accurate, complete, and current information when creating an account. You are responsible for keeping your login credentials confidential.',
        'You are responsible for all activity under your account. Notify us immediately if you suspect unauthorized access or misuse.',
        'By signing up, you agree to our Terms of Service, Privacy Policy, Cookie Policy, Refund Policy, Interview Transcription and AI Processing Consent, and Beta/Pilot Program Disclaimer.',
      ],
    },
    {
      heading: '5. The prepxai Service',
      paragraphs: ['prepxai may allow users to:'],
      bullets: [
        'Practice technical, behavioral, resume-based, and coding interviews with AI interviewers.',
        'Participate in coding interviews, coding exercises, and challenge workflows.',
        'Upload resumes for analysis or interview preparation.',
        'Use voice input for AI interview conversations.',
        'Receive AI-generated feedback, scores, reports, recommendations, summaries, and readiness insights.',
        'Participate in pilot, beta, early-access, institutional, or hiring programs.',
        'Use dashboards, progress tracking, reports, leaderboards, learning tools, and related platform features.',
      ],
      closingParagraphs: [
        'We may add, remove, modify, suspend, or discontinue any feature at any time, including during beta or pilot programs.',
      ],
    },
    {
      heading: '6. Interview Transcripts and Voice Input',
      paragraphs: [
        'prepxai may process spoken responses when users choose voice-enabled interview features. Speech may be temporarily processed for speech-to-text conversion.',
        'Only the resulting transcript is retained for interview functionality, evaluation, scoring, reporting, feedback, analytics, quality review, support, and platform improvement.',
        'prepxai does not retain raw audio after speech-to-text processing is completed.',
      ],
    },
    {
      heading: '7. Required AI Acknowledgement',
      paragraphs: [
        'Before signing up or starting an interview, you may be required to confirm the following acknowledgement:',
      ],
      quote: AI_ACKNOWLEDGEMENT,
      closingParagraphs: [
        'If you do not provide this acknowledgement, access to account creation or interview features may be restricted.',
      ],
    },
    {
      heading: '8. AI Output and No Professional Advice',
      paragraphs: [
        'prepxai uses AI systems to evaluate interview responses, transcripts, coding submissions, resume data, and related context.',
        'AI Output may be inaccurate, incomplete, biased, unsuitable, outdated, or misleading. AI systems may misunderstand your answer, overstate weaknesses, understate strengths, assign incorrect scores, or generate recommendations that do not fit your circumstances.',
        'AI Output is provided for preparation, education, practice, and decision-support only. It is not legal, HR, employment, educational, career, professional, medical, financial, or other regulated advice.',
        'You are responsible for reviewing AI Output independently before relying on it.',
      ],
    },
    {
      heading: '9. Hiring and Organizational Use',
      paragraphs: [
        'If an organization uses prepxai for hiring, screening, ranking, training, education, candidate evaluation, or employee assessment, that organization is responsible for complying with all applicable laws and internal policies.',
        'Organizations must provide required notices, obtain required consents, maintain appropriate human review, avoid unlawful discrimination, and avoid using AI Output as the sole basis for material employment, admissions, promotion, compensation, termination, or similar decisions.',
        'prepxai does not guarantee that AI Output is accurate, complete, fair, bias-free, legally compliant, or suitable for any hiring or employment decision.',
      ],
    },
    {
      heading: '10. User Content License',
      paragraphs: [
        'You retain ownership of your User Content. You grant prepxai a worldwide, non-exclusive, royalty-free, sublicensable, transferable license to host, process, transmit, analyze, display, reproduce, modify, create derivative works from, and otherwise use User Content as needed to provide, secure, support, improve, and operate the Service.',
        'This license includes the right to process resumes, transcripts, coding submissions, prompts, reports, and related content using AI systems and service providers.',
        'You represent that you have all rights, consents, and permissions needed to submit User Content and allow prepxai to process it.',
      ],
    },
    {
      heading: '11. prepxai Intellectual Property',
      paragraphs: [
        'prepxai and its software, platform design, workflows, prompts, scoring logic, interfaces, content, trademarks, logos, reports, documentation, model orchestration, and other materials are owned by or licensed to prepxai.',
        'Except as expressly allowed by these Terms, you may not copy, modify, distribute, sell, lease, sublicense, scrape, reverse engineer, decompile, create derivative works from, or attempt to extract the source code, prompts, model logic, or proprietary methods of prepxai.',
      ],
    },
    {
      heading: '12. Acceptable Use',
      paragraphs: ['You agree not to:'],
      bullets: [
        'Use prepxai for unlawful, harmful, fraudulent, abusive, deceptive, discriminatory, or exploitative purposes.',
        'Upload malware, harmful code, illegal content, infringing content, or content that violates third-party rights.',
        'Attempt to bypass authentication, security controls, rate limits, payment limits, usage limits, or access restrictions.',
        'Probe, scan, attack, overload, scrape, crawl, or disrupt prepxai infrastructure without written permission.',
        'Reverse engineer, extract prompts, harvest model behavior, or use prepxai to build a competing service without authorization.',
        'Misrepresent your identity, impersonate others, or submit information you do not have the right to submit.',
        'Use prepxai to make unlawful or solely automated hiring, screening, employment, admissions, or credit decisions.',
        'Submit confidential, sensitive, personal, or third-party information unless you have authority and consent to do so.',
        'Interfere with other users, candidates, organizations, interviews, assessments, or platform integrity.',
      ],
    },
    {
      heading: '13. Third-Party Services',
      paragraphs: [
        'prepxai may integrate with or rely on third-party services, including OpenAI, Google/Gemini, OpenRouter, Sarvam AI, cloud hosting providers, analytics providers, payment providers, email providers, and security providers.',
        'We are not responsible for third-party outages, errors, model behavior, data processing, terms, policies, or service limitations. Third-party services may change without notice.',
      ],
    },
    {
      heading: '14. Payments, Credits, and Plans',
      paragraphs: [
        'Some prepxai features may be free, paid, invite-only, usage-limited, credit-based, subscription-based, or available only during beta or pilot programs.',
        'Prices, features, quotas, credits, renewal terms, taxes, refunds, coupons, and cancellation rules may be shown at checkout or in the applicable plan details.',
        'Unless required by law or expressly stated otherwise, fees and consumed credits may be non-refundable.',
      ],
    },
    {
      heading: '15. Beta and Pilot Services',
      paragraphs: [
        'prepxai may be offered as a beta, pilot, preview, experimental, or early-access service. Beta Services may be incomplete, unstable, inaccurate, interrupted, changed, or discontinued at any time.',
        'Beta Services are provided "as is" and "as available" without warranties of any kind.',
      ],
    },
    {
      heading: '16. Suspension and Termination',
      paragraphs: [
        'We may suspend, limit, or terminate your access to prepxai at any time if we believe you violated these Terms, created risk for prepxai or others, misused the platform, failed to pay applicable fees, or if required for security, legal, operational, or compliance reasons.',
        'You may stop using prepxai at any time. Certain provisions of these Terms will survive termination, including intellectual property, disclaimers, limitation of liability, indemnity, dispute terms, and payment obligations.',
      ],
    },
    {
      heading: '17. Privacy',
      paragraphs: [
        'Our Privacy Policy explains how we collect, use, share, retain, and delete personal data. By using prepxai, you agree to our Privacy Policy.',
        'Users may request deletion of eligible stored transcripts, account data, resume data, reports, and other personal data by contacting us.',
      ],
    },
    {
      heading: '18. Disclaimers',
      paragraphs: [
        'To the maximum extent permitted by law, prepxai is provided "as is" and "as available" without warranties of any kind, whether express, implied, statutory, or otherwise.',
        'We do not warrant that prepxai will be uninterrupted, secure, error-free, accurate, complete, fair, bias-free, compliant with your requirements, or suitable for a particular purpose.',
        'We do not warrant that AI Output, scores, feedback, reports, recommendations, coding assessments, resume analysis, or readiness signals will be accurate, reliable, lawful, or useful for any specific decision.',
        'prepxai does not guarantee interview success, job offers, candidate selection, hiring outcomes, admissions outcomes, improved performance, career advancement, or any other result.',
      ],
    },
    {
      heading: '19. Limitation of Liability',
      paragraphs: [
        'To the maximum extent permitted by law, prepxai, its founders, employees, contractors, affiliates, licensors, and service providers will not be liable for indirect, incidental, special, consequential, exemplary, punitive, or enhanced damages, including loss of profits, revenue, data, goodwill, opportunity, employment prospects, business interruption, or reliance on AI Output.',
        'To the maximum extent permitted by law, our total aggregate liability for all claims relating to prepxai will not exceed the fees you paid to prepxai for the Service in the three months before the event giving rise to the claim, or INR 1,000 if you used the Service for free.',
        'Some laws may not allow certain limitations. In that case, the limitation will apply to the fullest extent permitted by law.',
      ],
    },
    {
      heading: '20. Indemnity',
      paragraphs: [
        'You agree to defend, indemnify, and hold harmless prepxai, its founders, employees, contractors, affiliates, licensors, and service providers from and against claims, losses, liabilities, damages, costs, and expenses arising from your use of prepxai, your User Content, your violation of these Terms, your violation of law, your hiring or organizational use, or your violation of third-party rights.',
      ],
    },
    {
      heading: '21. Governing Law and Jurisdiction',
      paragraphs: [
        'These Terms are governed by the laws of India, without regard to conflict-of-law rules.',
        'Subject to applicable law, courts located in Gautam Buddh Nagar, Uttar Pradesh, India will have exclusive jurisdiction over disputes arising from or relating to these Terms or prepxai.',
      ],
    },
    {
      heading: '22. Changes to These Terms',
      paragraphs: [
        'We may update these Terms from time to time. Updated Terms will be posted on the website with a revised last updated date.',
        'Your continued use of prepxai after updated Terms are posted means you accept the updated Terms, unless applicable law requires separate consent.',
      ],
    },
    companyContactSection,
  ],
};

export const cookiePolicy = {
  title: 'Cookie Policy',
  eyebrow: 'Legal',
  summary:
    'This Cookie Policy explains how prepxai uses cookies, local storage, and similar technologies for authentication, security, analytics, preferences, and performance.',
  lastUpdated: LEGAL_LAST_UPDATED,
  sections: [
    {
      heading: '1. Introduction',
      paragraphs: [
        `This Cookie Policy explains how ${LEGAL_ENTITY}, operating prepxai, uses cookies and similar technologies on our website, app, dashboard, interview workflows, coding environments, and related services.`,
        'This policy should be read with our Privacy Policy and Terms of Service.',
      ],
    },
    {
      heading: '2. Definitions',
      paragraphs: ['For this Cookie Policy:'],
      bullets: [
        '"Cookies" are small files stored on your browser or device.',
        '"Local Storage" means browser-based storage used to save settings, session data, or cached application data.',
        '"Pixels" and "Tags" are small code snippets that help measure activity, performance, or campaign effectiveness.',
        '"SDKs" are software tools that may collect technical or usage data inside an application.',
        '"Similar Technologies" include cookies, local storage, session storage, pixels, tags, beacons, SDKs, identifiers, and analytics tools.',
      ],
    },
    {
      heading: '3. Why We Use Cookies',
      paragraphs: ['We use cookies and similar technologies to:'],
      bullets: [
        'Keep users signed in and manage secure sessions.',
        'Protect accounts, prevent fraud, detect abuse, and support platform security.',
        'Remember user preferences and cookie choices.',
        'Enable interview, coding, dashboard, and product functionality.',
        'Measure usage, performance, errors, and reliability.',
        'Improve prepxai features, user experience, and product quality.',
        'Support caching and faster page loads.',
        'Understand marketing or referral performance where applicable.',
      ],
    },
    {
      heading: '4. Types of Cookies We Use',
      paragraphs: ['prepxai may use the following categories:'],
      bullets: [
        'Essential cookies: required for login, authentication, session management, security, fraud prevention, cookie consent, and core platform operation.',
        'Performance and caching cookies: used to make the app faster, remember interface state, support offline or cached behavior, and improve reliability.',
        'Analytics cookies: used to understand feature usage, page visits, session duration, errors, browser and device information, and product performance.',
        'Preference cookies: used to remember settings such as theme, language, saved options, or other interface preferences.',
        'Marketing cookies: used only where enabled to understand campaign performance, referrals, or relevant product communications.',
      ],
    },
    {
      heading: '5. Essential Cookies',
      paragraphs: [
        'Essential cookies are necessary for prepxai to work. They may be used for authentication, security, load balancing, CSRF protection, fraud prevention, account access, and remembering cookie preferences.',
        'You cannot disable essential cookies through our cookie banner because the service may not function without them. You can block them through your browser, but prepxai may become unavailable or unreliable.',
      ],
    },
    {
      heading: '6. Analytics and Performance',
      paragraphs: [
        'Analytics and performance technologies help us understand how users interact with prepxai. They may collect technical information such as browser type, device type, pages visited, features used, errors, duration, and approximate location inferred from technical information.',
        'We use this information to improve the platform, investigate bugs, maintain reliability, understand adoption, and make product decisions.',
      ],
    },
    {
      heading: '7. Third-Party Technologies',
      paragraphs: [
        'Some cookies or similar technologies may be provided by third-party service providers such as hosting, analytics, monitoring, customer support, email, payment, and security providers.',
        'Third-party providers may process technical and usage information according to their own policies and our agreements with them.',
      ],
    },
    {
      heading: '8. Managing Cookie Preferences',
      paragraphs: [
        'You can manage non-essential cookie choices through the prepxai cookie banner or preference panel where available.',
        'You can also control cookies through your browser settings. Browser controls may allow you to block cookies, delete cookies, clear local storage, or receive alerts before cookies are stored.',
      ],
    },
    {
      heading: '9. Impact of Disabling Cookies',
      paragraphs: [
        'If you disable cookies or clear local storage, some prepxai features may not work correctly, including login, account sessions, interview setup, coding interviews, saved preferences, cookie choices, security checks, analytics opt-outs, and dashboard performance.',
      ],
    },
    {
      heading: '10. Do Not Track',
      paragraphs: [
        'Some browsers offer "Do Not Track" signals. There is no uniform industry standard for responding to these signals, so prepxai may not respond to them in all cases.',
        'You can still manage cookies using our cookie banner and browser settings.',
      ],
    },
    {
      heading: '11. Updates to This Cookie Policy',
      paragraphs: [
        'We may update this Cookie Policy from time to time. Updated versions will be posted on our website with a revised last updated date.',
      ],
    },
    companyContactSection,
  ],
};

export const interviewConsent = {
  title: 'Interview Transcription and AI Processing Consent',
  eyebrow: 'Consent',
  summary:
    'This consent explains how prepxai processes interview inputs, speech-to-text transcripts, coding submissions, resumes, and AI-generated evaluation outputs.',
  lastUpdated: LEGAL_LAST_UPDATED,
  sections: [
    {
      heading: '1. Purpose of This Consent',
      paragraphs: [
        'prepxai provides AI-powered interview practice, coding interviews, resume-based analysis, voice-enabled interview workflows, and AI-generated feedback.',
        'This consent explains how interview inputs are processed when you use prepxai and what you acknowledge before starting an interview.',
      ],
    },
    {
      heading: '2. Interview Data Processed',
      paragraphs: ['When you participate in a prepxai interview, we may process:'],
      bullets: [
        'Typed interview responses.',
        'Spoken responses converted into text through speech-to-text processing.',
        'Interview transcripts.',
        'Coding submissions, code execution data, and test results.',
        'Resume data if uploaded or used for a resume-based interview.',
        'Interview configuration, timestamps, session metadata, and technical logs.',
        'AI-generated feedback, reports, scores, recommendations, and readiness insights.',
      ],
    },
    {
      heading: '3. Voice Input and Transcripts',
      paragraphs: [
        'If you use voice features, your speech may be temporarily processed to convert it into text for interview functionality.',
        'Only the resulting transcript is retained for evaluation, report generation, scoring, feedback, support, analytics, quality review, and platform improvement.',
        'prepxai does not retain raw audio after speech-to-text processing is completed.',
      ],
    },
    {
      heading: '4. AI Evaluation',
      paragraphs: [
        'prepxai uses AI systems and third-party AI providers to analyze interview transcripts, coding submissions, resume data, and interview context.',
        'AI systems may generate feedback, scores, strengths, weaknesses, reports, recommendations, suggested improvements, summaries, and readiness signals.',
      ],
    },
    {
      heading: '5. Required Acknowledgement',
      paragraphs: ['Before signup or interview start, you may be asked to confirm:'],
      quote: AI_ACKNOWLEDGEMENT,
      closingParagraphs: [
        'By checking this box or continuing to use interview features, you acknowledge the limits of AI evaluation and consent to the processing described in this consent.',
      ],
    },
    {
      heading: '6. AI Output Limitations',
      paragraphs: [
        'AI-generated feedback, scores, reports, recommendations, and summaries may contain inaccuracies, omissions, bias, hallucinations, outdated information, or incomplete reasoning.',
        'prepxai does not guarantee interview success, job offers, hiring outcomes, admissions outcomes, career advancement, skill improvement, or accuracy of AI evaluation.',
        'You should independently review all AI outputs before relying on them.',
      ],
    },
    {
      heading: '7. Human Review',
      paragraphs: [
        'AI Output should be reviewed by the user, candidate, coach, interviewer, recruiter, educator, or other appropriate human reviewer before it is used for any material decision.',
        'If an organization uses prepxai for hiring, screening, or assessment, that organization is responsible for lawful use, required notices, required consents, fairness, non-discrimination, and appropriate human review.',
      ],
    },
    {
      heading: '8. Third-Party Processing',
      paragraphs: ['prepxai may use third-party providers to process interview inputs and generate outputs, including:'],
      bullets: [
        'OpenAI.',
        'Google/Gemini.',
        'OpenRouter.',
        'Sarvam AI.',
        'Cloud hosting providers.',
        'Analytics, logging, infrastructure, security, and support providers.',
      ],
      closingParagraphs: [
        'These providers may process data as necessary to provide speech-to-text, AI analysis, hosting, security, infrastructure, and platform functionality.',
      ],
    },
    {
      heading: '9. Data Retention and Deletion',
      paragraphs: [
        'prepxai may retain account information, interview transcripts, coding submissions, interview reports, scores, resume data if uploaded, and usage analytics.',
        `You may request deletion of stored transcripts, account data, resume data, and eligible interview records by contacting ${CONTACT_EMAIL}.`,
        'We may retain limited information where required for legal, tax, audit, security, fraud prevention, compliance, backup, or dispute resolution purposes.',
      ],
    },
    {
      heading: '10. Withdrawal of Consent',
      paragraphs: [
        `You may withdraw consent by contacting ${CONTACT_EMAIL}. Withdrawal of consent may limit or prevent access to prepxai interview, transcript, reporting, resume, and AI evaluation features.`,
      ],
    },
    companyContactSection,
  ],
};

export const betaDisclaimer = {
  title: 'Beta and Pilot Program Disclaimer',
  eyebrow: 'Beta',
  summary:
    'This disclaimer applies to prepxai pilots, beta programs, early-access releases, trials, experiments, and limited-feature launches.',
  lastUpdated: LEGAL_LAST_UPDATED,
  sections: [
    {
      heading: '1. Beta and Pilot Status',
      paragraphs: [
        'prepxai may be offered as a pilot, beta, alpha, preview, trial, early-access, research, institutional, or experimental service.',
        'The platform is under active development. Features, availability, pricing, limits, reports, model providers, and workflows may change at any time.',
      ],
    },
    {
      heading: '2. Service Provided As Is',
      paragraphs: [
        'During beta or pilot periods, prepxai is provided "as is" and "as available".',
        'We do not promise that the service will be uninterrupted, secure, accurate, complete, reliable, timely, error-free, or suitable for your specific purpose.',
      ],
    },
    {
      heading: '3. Possible Issues',
      paragraphs: ['During beta or pilot use, you may experience:'],
      bullets: [
        'Bugs, errors, crashes, downtime, interruptions, or slow performance.',
        'Incomplete, experimental, changed, or removed features.',
        'Delayed, failed, or inaccurate AI responses.',
        'Incorrect feedback, scores, recommendations, summaries, or reports.',
        'Coding environment issues, failed test execution, or incorrect code analysis.',
        'Speech-to-text limitations, transcription errors, or browser compatibility issues.',
        'Data processing limitations, analytics limitations, or report-generation issues.',
        'Feature limits, eligibility changes, invite restrictions, or usage caps.',
      ],
    },
    {
      heading: '4. AI Output Limitations',
      paragraphs: [
        'prepxai uses AI systems to generate feedback, scores, reports, recommendations, and evaluations.',
        'AI Output may contain inaccuracies, omissions, bias, hallucinations, or incomplete reasoning.',
        'You should not rely on prepxai as the sole basis for employment, hiring, screening, education, admissions, career, compensation, promotion, or professional decisions.',
      ],
    },
    {
      heading: '5. No Outcome Guarantee',
      paragraphs: ['prepxai does not guarantee:'],
      bullets: [
        'Interview success.',
        'Job offers.',
        'Candidate selection.',
        'Hiring outcomes.',
        'Admissions outcomes.',
        'Improved scores.',
        'Career advancement.',
        'Accuracy, fairness, or completeness of AI evaluation.',
        'Suitability for any specific role, employer, institution, or interview process.',
      ],
    },
    {
      heading: '6. Changes During Beta',
      paragraphs: [
        'We may add, remove, modify, suspend, or discontinue features at any time during beta or pilot periods.',
        'We may also change access limits, pricing, eligibility, supported browsers, supported model providers, data processing methods, retention practices, and supported features.',
      ],
    },
    {
      heading: '7. User Data During Beta',
      paragraphs: [
        'prepxai may store account information, interview transcripts, coding submissions, interview reports and scores, resume data if uploaded, and usage analytics.',
        'If you use voice features, speech may be temporarily processed for speech-to-text conversion. Only the resulting transcript is retained for interview functionality and related purposes.',
        `You may request deletion of stored transcripts and account data by contacting ${CONTACT_EMAIL}.`,
      ],
    },
    {
      heading: '8. Feedback',
      paragraphs: [
        'You may provide feedback, suggestions, bug reports, interview comments, model-quality notes, or ideas about prepxai.',
        'By submitting feedback, you grant prepxai a perpetual, worldwide, royalty-free, transferable, sublicensable license to use, copy, modify, distribute, commercialize, and otherwise exploit that feedback without restriction or compensation.',
      ],
    },
    {
      heading: '9. Confidentiality',
      paragraphs: [
        'Some pilot or beta features may be confidential, invite-only, or pre-release. If we mark information as confidential or provide access under a private pilot, you agree not to disclose non-public features, screenshots, workflows, pricing, prompts, model behavior, or internal communications without written permission.',
      ],
    },
    {
      heading: '10. Limitation of Liability',
      paragraphs: [
        'To the maximum extent permitted by law, prepxai will not be liable for losses arising from beta or pilot use, including loss of data, loss of opportunity, business interruption, reliance on AI Output, technical errors, incorrect scores, report issues, downtime, or feature changes.',
        'Your use of beta or pilot features is voluntary and at your own risk.',
      ],
    },
    {
      heading: '11. Eligibility',
      paragraphs: [
        'You must be at least 18 years old to participate in a prepxai beta or pilot program.',
        'If you are under 18, you may participate only with consent and supervision from a parent or legal guardian.',
      ],
    },
    companyContactSection,
  ],
};

export const refundPolicy = {
  title: 'Refund Policy',
  eyebrow: 'Legal',
  summary:
    'This Refund Policy explains payments, coin purchases, interview coin deductions, unused-coin refunds, and how to request support for billing issues.',
  lastUpdated: LEGAL_LAST_UPDATED,
  sections: [
    {
      heading: '1. Introduction',
      paragraphs: [
        `This Refund Policy applies to prepxai services operated by ${LEGAL_ENTITY}, including coin packs, subscriptions, structured plans, AI interview sessions, coding interviews, resume tools, beta programs, and pilot programs.`,
        'By purchasing coins, starting an interview, subscribing to a plan, or using paid features, you agree to this Refund Policy, our Terms of Service, Privacy Policy, Cookie Policy, Interview Transcription and AI Processing Consent, and Beta/Pilot Program Disclaimer.',
      ],
    },
    {
      heading: '2. Definitions',
      paragraphs: ['For this Refund Policy:'],
      bullets: [
        '"Coin" means a prepxai platform credit used to access coin-based features such as interviews or practice sessions.',
        '"Coin Balance" means the number of coins available in your prepxai account.',
        '"Coin Pack" means a paid package that adds coins to your account.',
        '"Expected Duration" means the interview duration selected or assigned before the session starts, such as 30, 45, or 60 minutes.',
        '"Used Duration" means the portion of the interview actually used, measured by prepxai system records, session state, elapsed time, speech-to-text activity, text activity, coding activity, or other reliable platform logs.',
        '"Unused Coins" means coins corresponding to unused minutes of a coin-based interview session.',
        '"Platform Failure" means a material prepxai technical issue that prevents normal use of a paid feature and is verified through logs or support review.',
        '"Refund" may mean a return of money to the original payment method, a reversal of a charge, or a credit of coins to your prepxai account, depending on the situation.',
      ],
    },
    {
      heading: '3. Coin Deduction for Interviews',
      paragraphs: [
        'Coin-based interviews deduct coins based on the expected duration shown before the interview starts.',
        'Unless the product screen states otherwise, the current interview rate is 10 coins per minute. For example, a 30-minute interview may deduct 300 coins, a 45-minute interview may deduct 450 coins, and a 60-minute interview may deduct 600 coins.',
        'prepxai may change coin pricing, coins-per-minute rates, free coins, promotional coins, plan benefits, and feature access from time to time. The amount shown at interview start controls the deduction for that session.',
      ],
    },
    {
      heading: '4. Unused-Coin Refunds for Incomplete Interviews',
      paragraphs: [
        'If a coin-based interview is not completed for the expected duration, prepxai may refund unused coins corresponding to the unused minutes of the session.',
        'Unused-coin refunds are generally credited back to your prepxai account as coins, not as cash.',
        'For example, if a 45-minute interview deducts 450 coins at 10 coins per minute and the system records only 20 minutes of use, prepxai may credit back 250 unused coins for the remaining 25 minutes.',
      ],
      bullets: [
        'If the interview is started but ended early, unused coins may be refunded based on unused minutes.',
        'If the interview session is created but never actually starts, prepxai may refund the full coin amount for that session.',
        'If the browser crashes, the connection drops, or the user leaves without properly ending the session, prepxai may use system cleanup jobs and session logs to calculate and credit unused coins.',
        'If system records show the full expected duration was used, no unused-coin refund is generally available.',
      ],
    },
    {
      heading: '5. Situations Where Coin Refunds May Be Denied',
      paragraphs: ['prepxai may deny or reduce a coin refund where:'],
      bullets: [
        'System records show the expected duration was substantially used.',
        'The session was completed and a report, score, transcript, or result was generated for the full or substantially full duration.',
        'The issue was caused by user device limitations, microphone permissions, unsupported browsers, weak internet connection, third-party browser extensions, or user-side setup problems.',
        'The user repeatedly starts and abandons sessions in a way that appears abusive or intended to exploit refunds.',
        'The session was part of a structured plan, challenge, free trial, promotional credit, coupon, beta test, or manually granted credit with different rules.',
        'The request cannot be verified through account records, payment records, support records, or platform logs.',
        'Refunding would violate applicable law, payment network rules, anti-fraud rules, or prepxai policies.',
      ],
    },
    {
      heading: '6. Payment Refunds for Coin Packs',
      paragraphs: [
        'Coin pack payments are generally non-refundable once coins are added to your account, unless required by law or approved by prepxai in its discretion.',
        'If coins purchased in a coin pack have not been used, you may contact us within 7 days of purchase and we may review the request case by case.',
        'Where a money refund is approved, we may deduct used coins, payment gateway fees, taxes, discounts, promotional credits, chargeback costs, or other non-recoverable amounts to the extent permitted by law.',
      ],
    },
    {
      heading: '7. Subscription and Plan Refunds',
      paragraphs: [
        'Subscription fees and structured plan fees are generally non-refundable once access begins, unless required by law or expressly stated at purchase.',
        'You may cancel a subscription, if subscriptions are enabled, and cancellation will take effect at the end of the current billing cycle unless the plan page states otherwise.',
        'No refund is generally provided for partial use, unused days, unused features, dissatisfaction with AI outputs, changes in personal circumstances, or failure to use the service.',
      ],
    },
    {
      heading: '8. Beta and Pilot Programs',
      paragraphs: [
        'Beta, pilot, preview, early-access, free, discounted, sponsored, institutional, or trial programs may have limited or no refund rights unless otherwise stated in writing.',
        'Because beta services are provided "as is" and may contain bugs, interruptions, and AI inaccuracies, participation in a beta or pilot does not guarantee refunds, credits, job outcomes, interview success, or any particular result.',
      ],
    },
    {
      heading: '9. AI Output and Refunds',
      paragraphs: [
        'prepxai does not provide refunds merely because AI-generated feedback, scores, recommendations, or reports are unfavorable, unexpected, incomplete, or inaccurate.',
        'AI outputs may contain inaccuracies, and users must review them independently. This limitation does not affect eligible unused-coin refunds for incomplete coin-based interview duration.',
      ],
    },
    {
      heading: '10. Failed, Duplicate, or Unauthorized Payments',
      paragraphs: [
        'If you believe you were charged twice, charged incorrectly, or charged without authorization, contact us promptly with transaction details.',
        'We may ask for your account email, payment ID, order ID, screenshots, bank reference number, invoice, or other information needed to investigate.',
      ],
    },
    {
      heading: '11. How to Request a Refund or Coin Credit',
      paragraphs: [
        `To request a refund or coin credit, contact ${CONTACT_EMAIL} with your account email, payment details if applicable, interview session ID if available, expected duration, approximate time used, screenshots if relevant, and a short explanation of the issue.`,
        'We may review account records, session logs, payment records, system status, and support history before deciding a request.',
      ],
    },
    {
      heading: '12. Processing Time',
      paragraphs: [
        'Approved coin credits may be applied to your prepxai account after review or through automated session cleanup.',
        'Approved money refunds, if any, may take additional time depending on the payment gateway, bank, card network, UPI provider, or other payment provider.',
      ],
    },
    {
      heading: '13. Chargebacks',
      paragraphs: [
        'If you initiate a chargeback or payment dispute, we may suspend access to paid features while the dispute is pending.',
        'Where a chargeback is found invalid or abusive, we may recover related costs, reverse granted coins, restrict future purchases, or terminate access to prepxai to the extent permitted by law.',
      ],
    },
    {
      heading: '14. Changes to This Refund Policy',
      paragraphs: [
        'We may update this Refund Policy from time to time. Updated versions will be posted on the website with a revised last updated date.',
        'The Refund Policy in effect at the time of purchase or session start will generally apply to that transaction or session, unless an updated policy is required by law or is more favorable to the user.',
      ],
    },
    companyContactSection,
  ],
};
